import { FormControlLabel, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { KeyIcon } from '@/shared/assets/icons/key-icon';
import { Checkbox } from '@/shared/components/ui/checkbox/checkbox';
import { TOptionalDeleteAction } from '@/shared/components/ui/confirm-delete/confirm-delete.types';
import { SelectedItemsContainer } from '@/shared/components/ui/selected-items-list/selected-items-list.styled';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';
import {
  LinkedSecretItemContainer,
  LinkedSecretsErrorContainer,
  LinkedSecretsTitleRow,
} from '@/shared/features/assets/delete-assets/use-linked-secrets-delete-action.styled';
import {
  useDeleteSecretMutation,
  useReadSecretQuery,
} from '@/shared/redux/api/assets/vaults/vaults-api';
import { isPermissionDeniedError } from '@/shared/redux/api/utils/error-handlers';

const LinkedSecretOption: React.FC<{
  secretId: string;
}> = ({ secretId }) => {
  const { t } = useTranslation();
  const { data, error, isFetching, isLoading } = useReadSecretQuery({
    secretId,
  });
  const secret = data?.readSecret;
  const isPending = isLoading || isFetching;
  const isPermissionDenied = !isPending && isPermissionDeniedError(error);
  const secretInfo = isPermissionDenied
    ? t('vault:restricted_secret')
    : secret
      ? `${secret.vault.name} / ${secret.name}`
      : isPending
        ? t('common:loading')
        : secretId;

  return (
    <LinkedSecretItemContainer $variant="default" $sizeVariant="compact">
      <Stack minWidth={0} width="100%">
        <TruncatedText
          text={secretInfo}
          variant="p5"
          maxLength={35}
          color="textSecondary"
        />
      </Stack>
    </LinkedSecretItemContainer>
  );
};

export const useLinkedSecretsDeleteAction = (
  secretIds: Array<string | undefined>,
  singleLinkedItemLabel?: string,
): TOptionalDeleteAction | undefined => {
  const { t } = useTranslation();
  const linkedSecretIds = useMemo(
    () =>
      Array.from(
        new Set(secretIds.filter((secretId): secretId is string => !!secretId)),
      ),
    [secretIds],
  );
  const [shouldDeleteLinkedSecrets, setShouldDeleteLinkedSecrets] =
    useState(false);
  const [deleteSecret] = useDeleteSecretMutation();
  const secretCount = linkedSecretIds.length;
  const hasLinkedSecrets = secretCount > 0;
  const isSingleLinkedSecret = secretCount === 1;
  const { isFetching, isLoading } = useReadSecretQuery(
    { secretId: linkedSecretIds[0] ?? '' },
    { skip: !isSingleLinkedSecret || !hasLinkedSecrets },
  );
  const isSecretPending = isLoading || isFetching;

  useEffect(() => {
    setShouldDeleteLinkedSecrets(false);
  }, [linkedSecretIds]);

  if (!hasLinkedSecrets) {
    return undefined;
  }

  const linkedSecretsTitle = isSingleLinkedSecret
    ? t('ui:confirm_delete_modal.linked_secret_single_title', {
        item: singleLinkedItemLabel ?? 'item',
      })
    : t('ui:confirm_delete_modal.linked_secret_multiple_title', {
        count: secretCount,
      });

  const linkedSecretsLabel = t(
    'ui:confirm_delete_modal.linked_secret_delete_associated',
    {
      count: secretCount,
    },
  );

  return {
    label: linkedSecretsLabel,
    details: (
      <Stack gap="0.75rem" width="100%">
        <LinkedSecretsErrorContainer>
          <LinkedSecretsTitleRow>
            <KeyIcon />
            <Typography variant="p5" color="textSecondary">
              {linkedSecretsTitle}
            </Typography>
          </LinkedSecretsTitleRow>
          <SelectedItemsContainer gap="0.25rem" maxHeight="10rem">
            {linkedSecretIds.map((secretId) => (
              <LinkedSecretOption key={secretId} secretId={secretId} />
            ))}
          </SelectedItemsContainer>
          <FormControlLabel
            disabled={isSingleLinkedSecret && isSecretPending}
            control={
              <Checkbox
                checked={shouldDeleteLinkedSecrets}
                onChange={(_, checked) => {
                  setShouldDeleteLinkedSecrets(checked);
                }}
              />
            }
            label={
              <Typography variant="p5" color="textSecondary">
                {linkedSecretsLabel}
              </Typography>
            }
          />
        </LinkedSecretsErrorContainer>
      </Stack>
    ),
    mutation: async () => {
      await Promise.all(
        linkedSecretIds.map((secretId) => deleteSecret({ secretId }).unwrap()),
      );
    },
    successMessage: t(
      'ui:confirm_delete_modal.linked_secret_delete_success_count',
      { count: secretCount },
    ),
    errorMessage: t(
      'ui:confirm_delete_modal.linked_secret_delete_error_count',
      { count: secretCount },
    ),
    checked: shouldDeleteLinkedSecrets,
    onChange: setShouldDeleteLinkedSecrets,
    disabled: isSingleLinkedSecret && isSecretPending,
  };
};
